import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export type UserStatus = "active" | "suspended" | "locked";
export type RoleSlug = "admin" | "operator" | "analyst" | "customer";

export const ALL_ROLES: ReadonlyArray<RoleSlug> = ["admin", "operator", "analyst", "customer"];

export interface UserRecord {
    id: string;
    tenantId?: string;
    email: string;
    status: UserStatus;
    roles: RoleSlug[];
    createdAt: string;
}

const USERS_KEY = ["admin", "users"] as const;

export function useUsers() {
    return useQuery({
        queryKey: USERS_KEY,
        queryFn: () => api.get<{ users: UserRecord[] }>("/api/v2/users").then((d) => d.users),
        staleTime: 10_000,
    });
}

interface CreateUserInput {
    email: string;
    roles: RoleSlug[];
    password?: string;
}

export function useCreateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateUserInput) =>
            api.post<{ user: UserRecord; temporaryPassword?: string }>("/api/v2/users", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: USERS_KEY });
        },
    });
}

interface UpdateUserInput {
    id: string;
    status?: UserStatus;
    roles?: RoleSlug[];
}

export function useUpdateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...patch }: UpdateUserInput) =>
            api.patch<{ user: UserRecord }>(`/api/v2/users/${id}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: USERS_KEY });
        },
    });
}

export function useDeleteUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete<void>(`/api/v2/users/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: USERS_KEY });
        },
    });
}

export function useResetUserPassword() {
    return useMutation({
        mutationFn: (id: string) =>
            api.post<{ temporaryPassword: string }>(`/api/v2/users/${id}/reset-password`),
    });
}
